import type { BusinessType, KycDocument, KycRecord, KycStatus } from "@/types/kyc";
export function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export const KYC_STATUS_META: Record<KycStatus, { label: string; text: string; bg: string }> = {
  NOT_STARTED: { label: "Not Started", text: "text-[#475569]", bg: "bg-[#F1F5F9]" },
  DRAFT: { label: "Draft", text: "text-[#475569]", bg: "bg-[#F1F5F9]" },
  SUBMITTED: { label: "Submitted", text: "text-[#0284C7]", bg: "bg-[#F0F9FF]" },
  UNDER_REVIEW: { label: "Under Review", text: "text-[#D97706]", bg: "bg-[#FFFBEB]" },
  APPROVED: { label: "Approved", text: "text-[#059669]", bg: "bg-[#ECFDF5]" },
  REJECTED: { label: "Rejected", text: "text-[#DC2626]", bg: "bg-[#FEF2F2]" },
};

export const BUSINESS_TYPE_OPTIONS = [
  { value: "Proprietorship", label: "Proprietorship" },
  { value: "Partnership", label: "Partnership" },
  { value: "Private Limited", label: "Private Limited" },
  { value: "LLP", label: "LLP" },
];

// Business + personal documents required for each business type.
export const REQUIRED_DOCS: Record<BusinessType, string[]> = {
  Proprietorship: [
    "GST Certificate or Udyam Registration",
    "Business PAN Card",
    "Aadhaar Card — Front",
    "Aadhaar Card — Back",
    "PAN Card",
    "Selfie",
  ],
  Partnership: [
    "Partnership Deed",
    "GST Certificate",
    "Business PAN Card",
    "Aadhaar Card — Front",
    "Aadhaar Card — Back",
    "PAN Card",
    "Selfie",
  ],
  "Private Limited": [
    "Certificate of Incorporation",
    "GST Certificate",
    "Company PAN Card",
    "Aadhaar Card — Front",
    "Aadhaar Card — Back",
    "PAN Card",
    "Selfie",
  ],
  LLP: [
    "Certificate of Incorporation",
    "GST Certificate",
    "Company PAN Card",
    "Aadhaar Card — Front",
    "Aadhaar Card — Back",
    "PAN Card",
    "Selfie",
  ],
};

export const OPTIONAL_DOC_LABELS = ["Other Supporting Document", "Cancelled Cheque or Bank Statement"];

export function requiredDocCount(businessType: BusinessType | string | null): number {
  if (!businessType) return 0;
  return REQUIRED_DOCS[businessType as BusinessType]?.length ?? 0;
}

function norm(s: string): string {
  return s.toLowerCase().replace(/[\s\-—–]+/g, "");
}

export function matchDoc(record: KycRecord, label: string): KycDocument | undefined {
  if (!record || !Array.isArray(record.documents)) return undefined;
  return record.documents.find((d) => {
    const docType = d.docType || (d as any).type || "";
    return norm(docType) === norm(label);
  });
}

export function requiredUploadedCount(record: KycRecord): number {
  if (!record.businessType) return 0;
  const required = REQUIRED_DOCS[record.businessType as BusinessType] ?? [];
  return required.filter((label) => !!matchDoc(record, label)).length;
}

export function pendingKycCount(records: KycRecord[]): number {
  return records.filter((r) => r.status === "SUBMITTED" || r.status === "UNDER_REVIEW").length;
}

const BRANCH_MAP: Record<string, string> = {
  "HDFC Bank": "Connaught Place Branch, Delhi",
  "ICICI Bank": "Bandra Kurla Complex Branch, Mumbai",
  "State Bank of India": "Rohini Sector 24 Branch, Delhi",
  "Axis Bank": "Koramangala Branch, Bengaluru",
  "Kotak Mahindra Bank": "Fort Branch, Mumbai",
  "Punjab National Bank": "Karol Bagh Branch, New Delhi",
  "Bank of Baroda": "Alipore Branch, Kolkata",
  "Canara Bank": "MG Road Branch, Bengaluru",
  "Union Bank of India": "Gariahat Branch, Kolkata",
  "IndusInd Bank": "Andheri West Branch, Mumbai",
  "Yes Bank": "Worli Branch, Mumbai",
  "IDFC First Bank": "Powai Branch, Mumbai",
};

export function resolveBranch(bankName: string): string {
  return BRANCH_MAP[bankName] ?? bankName;
}